import { Button } from '@/components/v2/Button';
import { ComboBox, ComboBoxItem } from '@/components/v2/Form/ComboBox/ComboBox';
import { Form } from '@/components/v2/Form/Form';
import { MultiSelectItem } from '@/components/v2/Form/ListBox';
import { MultiSelect } from '@/components/v2/Form/MultiSelect/MultiSelect';
import { useGetEntireOrgList } from '@/features/organizations';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useCreateConferenceUserAssignment } from '../api/conferenceuserassignments/createConferenceUserAssignment';
import OncolensToast from '@/components/OncolensToast';
import { useGetConferencesByOrg } from '@/features/conferences/api/conferences';
import { conferenceDisplayName } from '@/utils/conferenceDisplayName';

const formSchema = z.object({
  organizationId: z.string().min(1),
  conferenceIds: z.array(z.string()).min(1),
  roleId: z.string().optional(),
});

interface Props {
  userId: string;
  conferenceAccessItems: number | undefined;
}

export const UserConferenceAdd = ({ userId, conferenceAccessItems }: Props) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      organizationId: '',
      conferenceIds: [],
      roleId: '',
    },
    mode: 'onChange',
  });
  const { isValid } = form.formState;
  const organizationId = form.watch('organizationId');
  const { data: organizations } = useGetEntireOrgList();
  const { data: conferences } = useGetConferencesByOrg(organizationId, {
    enabled: !!organizationId,
  });

  const addConferenceUserAssignment = useCreateConferenceUserAssignment();

  const orgItems =
    organizations?.map((org) => ({
      id: org.id,
      name: org.name,
    })) ?? [];

  const handleSubmit = () => {
    const values = form.getValues();

    Promise.all(
      values.conferenceIds.map((conferenceId) =>
        addConferenceUserAssignment.mutateAsync({
          userId,
          conferenceId,
        }),
      ),
    ).then(() => {
      form.reset();
      OncolensToast.success('Conference(s) added successfully');
    });
  };

  return (
    <Form {...form}>
      <div className="flex gap-4 items-center w-full py-4 px-6 border-b boder-b-[#D4D4D8]">
        <div data-cy="conference-access-table-header" className="text-sm text-zinc-800 font-bold">
          Conference access{' '}
          {conferenceAccessItems && conferenceAccessItems > 0 ? (
            <span className="font-normal">({conferenceAccessItems})</span>
          ) : (
            ''
          )}
        </div>
        <div>
          <Controller
            control={form.control}
            name="organizationId"
            render={({ field }) => (
              <ComboBox
                className="min-w-[275px]"
                selectedKey={field.value}
                onSelectionChange={(key) => {
                  field.onChange(key);
                  form.setValue('conferenceIds', []);
                }}
                placeholder="Type host organization"
                items={orgItems}>
                {orgItems?.map((org) => (
                  <ComboBoxItem key={org.id} id={org.id}>
                    {org.name}
                  </ComboBoxItem>
                ))}
              </ComboBox>
            )}
          />
        </div>
        <div>
          <Controller
            control={form.control}
            name="conferenceIds"
            render={({ field }) => (
              <MultiSelect
                isDisabled={!organizationId}
                aria-label="Select conference name(s)"
                placeholder="Select conference name(s)"
                items={conferences ?? []}
                keyProp={'id'}
                selectedKeys={field.value}
                onSelectionChange={(keys) => field.onChange(Array.from(keys))}
                selectedItemsRender={(props) => (
                  <span
                    className={`overflow-hidden ${
                      props.isPlaceholder ? 'text-zinc-400' : 'text-zinc-800'
                    }`}>
                    {props.isPlaceholder
                      ? 'Select conference name(s)'
                      : `Selected ${props.selectedItems?.length} conferences`}
                  </span>
                )}>
                {(conference) => (
                  <MultiSelectItem key={conference.id} id={conference.id}>
                    {conferenceDisplayName(conference.name, conference.status)}
                  </MultiSelectItem>
                )}
              </MultiSelect>
            )}
          />
        </div>
        <div className="flex-1"></div>
        <div>
          <Button
            disabled={!isValid}
            variant="primary"
            testId="go-back-button"
            onClickHandler={() => handleSubmit()}>
            Add
          </Button>
        </div>
      </div>
    </Form>
  );
};
